/**
 * Gating for the silent token-recovery attempt in TokenInvalidBanner.
 *
 * Two failure modes pull in opposite directions and both are real:
 *
 *   - Retrying on every 60 s poll. When Shopify refuses the exchange the
 *     answer will not change until the merchant does something, so repeating
 *     the call forever is pure noise against Shopify's OAuth endpoint.
 *
 *   - Never retrying again. Gating on the store id alone means that once a
 *     store has had its one attempt, a *later*, entirely separate breakage
 *     can never be repaired automatically — the merchant is left with a
 *     silently broken app and, because recovery is what decides whether the
 *     banner appears, no banner either.
 *
 * The resolution is to gate on the invalidation episode rather than on the
 * store: `token_invalid_at` is rewritten by _mark_token_invalid every time a
 * store is marked, so a new breakage carries a new timestamp and earns a
 * fresh attempt, while a single breakage is attempted exactly once.
 */

/**
 * Identity of one invalidation episode. Returns null when there is no store
 * to key on, which callers treat as "nothing to attempt".
 *
 * `invalidAt` missing falls back to a constant, which degrades to one attempt
 * per store rather than to unbounded retries — the safe direction. The
 * banner's reset on a healthy poll is what re-arms that case.
 */
export function recoveryCycleKey(storeId, invalidAt) {
  if (!storeId) return null;
  return `${storeId}:${invalidAt || 'unknown'}`;
}

/**
 * Whether automatic recovery should run for the invalidation just observed.
 *
 * @param lastAttemptedKey the key recovery was last attempted for, or null
 * @param storeId          the current store
 * @param invalidAt        token_invalid_at from /auth/token-status
 */
export function shouldAttemptRecovery(lastAttemptedKey, storeId, invalidAt) {
  const key = recoveryCycleKey(storeId, invalidAt);
  return key !== null && key !== lastAttemptedKey;
}
